import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  Pagination,
  Alert,
  CircularProgress,
  Switch,
  InputAdornment,
  FormControlLabel,
  Stack,
  Fab,
  useTheme,
  useMediaQuery,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Drawer,
  Divider,
  alpha
} from '@mui/material';

import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Photo as PhotoIcon,
  Save as SaveIcon,
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  Menu as MenuIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';

import api from './api';

const BASE_URL = 'https://dhahabjw.com';

// Simple Image Upload Component
const ImageUpload = ({ images, onImagesChange, maxImages = 10 }) => {
  const fileInputRef = useRef(null);
  const [uploadError, setUploadError] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setUploadError('');

    if (images.length + files.length > maxImages) {
      setUploadError(`Maximum ${maxImages} images allowed`);
      return;
    }

    const validFiles = [];
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        setUploadError('Only image files are allowed');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setUploadError('File size must be less than 5MB');
        return;
      }

      validFiles.push(file);
    }

    const newImages = validFiles.map((file, index) => ({
      file,
      url: URL.createObjectURL(file),
      alt: `Product image ${images.length + index + 1}`,
      isPrimary: images.length === 0 && index === 0,
      isNew: true
    }));

    onImagesChange([...images, ...newImages]);
  };

  const handleRemoveImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    if (images[index].isPrimary && newImages.length > 0) {
      newImages[0].isPrimary = true;
    }
    onImagesChange(newImages);
  };

  const handleSetPrimary = (index) => {
    const newImages = images.map((img, i) => ({
      ...img,
      isPrimary: i === index
    }));
    onImagesChange(newImages);
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Product Images
        </Typography>
        <Button
          variant="outlined"
          onClick={() => fileInputRef.current?.click()}
          disabled={images.length >= maxImages}
          size="small"
          startIcon={<UploadIcon />}
          sx={{ borderRadius: 2 }}
        >
          Upload ({images.length}/{maxImages})
        </Button>
      </Box>

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        multiple
        accept="image/*"
        style={{ display: 'none' }}
      />

      {uploadError && (
        <Alert
          severity="error"
          onClose={() => setUploadError('')}
          sx={{ mb: 2 }}
        >
          {uploadError}
        </Alert>
      )}

      {images.length > 0 && (
        <Grid container spacing={2}>
          {images.map((image, index) => (
            <Grid item xs={6} sm={4} md={3} key={index}>
              <Box
                sx={{
                  position: 'relative',
                  paddingBottom: '100%',
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: image.isPrimary ? 'primary.main' : 'divider',
                  boxShadow: image.isPrimary ? 1 : 0
                }}
              >
                <img
                  src={image.url}
                  alt={image.alt}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />

                {image.isPrimary && (
                  <Chip
                    label="Primary"
                    size="small"
                    color="primary"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      fontSize: '0.75rem'
                    }}
                  />
                )}

                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    display: 'flex',
                    gap: 0.5
                  }}
                >
                  <IconButton
                    onClick={() => handleSetPrimary(index)}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(0, 0, 0, 0.7)',
                      color: 'white',
                      '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.8)' },
                      width: 28,
                      height: 28
                    }}
                  >
                    {image.isPrimary ? <StarIcon sx={{ fontSize: 16 }} /> : <StarBorderIcon sx={{ fontSize: 16 }} />}
                  </IconButton>

                  <IconButton
                    onClick={() => handleRemoveImage(index)}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(244, 67, 54, 0.9)',
                      color: 'white',
                      '&:hover': { bgcolor: 'rgba(244, 67, 54, 1)' },
                      width: 28,
                      height: 28
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

// Simplified Product Form
const ProductForm = ({ open, onClose, product, onSave, loading, categories = [] }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [formData, setFormData] = useState({
    name: { en: '', ar: '' },
    description: { en: '', ar: '' },
    category: '',
    metal: '',
    karat: '',
    weight: '',
    makingCharges: 0,
    specifications: {
      dimensions: '',
      style: ''
    },
    inventory: {
      inStock: true,
      quantity: 1,
      lowStockThreshold: 5
    },
    pricing: {
      discount: 0,
      isOnSale: false
    },
    isActive: true
  });

  const [images, setImages] = useState([]);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');

  const metals = ['gold', 'silver'];
  const karats = ['18K', '21K', '22K', '24K', '925'];

  useEffect(() => {
    setFormError('');
    if (product) {
      setFormData(product);
      const existingImages = (product.images || []).map(img => ({
        ...img,
        isNew: false
      }));
      setImages(existingImages);
    } else {
      setFormData({
        name: { en: '', ar: '' },
        description: { en: '', ar: '' },
        category: '',
        metal: '',
        karat: '',
        weight: '',
        makingCharges: 0,
        specifications: {
          dimensions: '',
          style: ''
        },
        inventory: {
          inStock: true,
          quantity: 1,
          lowStockThreshold: 5
        },
        pricing: {
          discount: 0,
          isOnSale: false
        },
        isActive: true
      });
      setImages([]);
    }
    setErrors({});
  }, [product, open]);

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev };
      const fieldPath = field.split('.');
      if (fieldPath.length === 1) {
        newData[fieldPath[0]] = value;
      } else if (fieldPath.length === 2) {
        newData[fieldPath[0]] = {
          ...newData[fieldPath[0]],
          [fieldPath[1]]: value
        };
      } else if (fieldPath.length === 3) {
        newData[fieldPath[0]] = {
          ...newData[fieldPath[0]],
          [fieldPath[1]]: {
            ...newData[fieldPath[0]][fieldPath[1]],
            [fieldPath[2]]: value
          }
        };
      }
      return newData;
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name?.en?.trim()) newErrors['name.en'] = 'English name is required';
    if (!formData.name?.ar?.trim()) newErrors['name.ar'] = 'Arabic name is required';
    if (!formData.description?.en?.trim()) newErrors['description.en'] = 'English description is required';
    if (!formData.description?.ar?.trim()) newErrors['description.ar'] = 'Arabic description is required';
    if (!formData.category) newErrors.category = 'Category is required';
    if (!formData.metal) newErrors.metal = 'Metal is required';
    // Karat only applies to gold; the API stores it as optional
    if (formData.metal === 'gold' && !formData.karat) newErrors.karat = 'Karat is required for gold';

    const weight = parseFloat(formData.weight);
    if (!weight || weight <= 0) newErrors.weight = 'Weight must be greater than 0';

    setErrors(newErrors);
    return newErrors;
  };

  const FIELD_LABELS = {
    'name.en': 'English name',
    'name.ar': 'Arabic name',
    'description.en': 'English description',
    'description.ar': 'Arabic description',
    category: 'Category',
    metal: 'Metal',
    karat: 'Karat',
    weight: 'Weight'
  };

  const handleSubmit = async () => {
    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      // Name what is missing: these fields can be far down a long form, and a
      // button that silently does nothing looks like a broken screen
      const missing = Object.keys(validationErrors)
        .map((key) => FIELD_LABELS[key] || key)
        .join(', ');
      setFormError(`Please complete: ${missing}`);
      return;
    }

    setFormError('');

    try {
      const submitFormData = new FormData();

      // Basic fields
      submitFormData.append('name[en]', formData.name.en);
      submitFormData.append('name[ar]', formData.name.ar);
      submitFormData.append('description[en]', formData.description.en);
      submitFormData.append('description[ar]', formData.description.ar);
      submitFormData.append('category', formData.category);
      submitFormData.append('metal', formData.metal);
      submitFormData.append('karat', formData.karat);
      submitFormData.append('weight', formData.weight.toString());
      submitFormData.append('makingCharges', formData.makingCharges.toString());

      // Complex fields
      submitFormData.append('specifications[dimensions]', formData.specifications.dimensions || '');
      submitFormData.append('specifications[style]', formData.specifications.style || '');
      submitFormData.append('inventory[inStock]', formData.inventory.inStock.toString());
      submitFormData.append('inventory[quantity]', formData.inventory.quantity.toString());
      submitFormData.append('inventory[lowStockThreshold]', formData.inventory.lowStockThreshold.toString());
      submitFormData.append('pricing[discount]', formData.pricing.discount || 0);
      submitFormData.append('pricing[isOnSale]', formData.pricing.isOnSale || false);
      submitFormData.append('isActive', formData.isActive.toString());

      // Images
      images.forEach((image, index) => {
        if (image.isNew && image.file) {
          submitFormData.append('images', image.file);
          submitFormData.append(`image_${index}_isPrimary`, image.isPrimary.toString());
          submitFormData.append(`image_${index}_alt`, image.alt);
        }
      });

      if (product) {
        submitFormData.append('productId', product._id);
        const existingImages = images.filter(img => !img.isNew);
        submitFormData.append('existingImages', JSON.stringify(existingImages));
      }

      await onSave(submitFormData);
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      setErrors({ submit: 'An error occurred while saving the product' });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: { borderRadius: isMobile ? 0 : 2 }
      }}
    >
      <DialogTitle>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {product ? 'Edit Product' : 'Add New Product'}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Box sx={{ mt: 2 }}>
          {/* Images Section */}
          <ImageUpload
            images={images}
            onImagesChange={setImages}
            maxImages={10}
          />

          <Divider sx={{ my: 3 }} />

          {/* Basic Information */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Basic Information
          </Typography>

          {formError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFormError('')}>
              {formError}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Product Name (English)"
                value={formData.name.en}
                onChange={(e) => handleInputChange('name.en', e.target.value)}
                error={!!errors['name.en']}
                helperText={errors['name.en']}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Product Name (Arabic)"
                value={formData.name.ar}
                onChange={(e) => handleInputChange('name.ar', e.target.value)}
                error={!!errors['name.ar']}
                helperText={errors['name.ar']}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description (English)"
                value={formData.description.en}
                onChange={(e) => handleInputChange('description.en', e.target.value)}
                error={!!errors['description.en']}
                helperText={errors['description.en']}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description (Arabic)"
                value={formData.description.ar}
                onChange={(e) => handleInputChange('description.ar', e.target.value)}
                error={!!errors['description.ar']}
                helperText={errors['description.ar']}
                size="small"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Product Details */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Product Details
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small" error={!!errors.category}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  label="Category"
                  disabled={categories.length === 0}
                >
                  {categories.map(cat => (
                    <MenuItem key={cat._id} value={cat._id}>
                      {cat.name?.en || cat.name?.ar || 'Unnamed Category'}
                    </MenuItem>
                  ))}
                  {categories.length === 0 && (
                    <MenuItem disabled>
                      No categories available
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small" error={!!errors.metal}>
                <InputLabel>Metal</InputLabel>
                <Select
                  value={formData.metal}
                  onChange={(e) => handleInputChange('metal', e.target.value)}
                  label="Metal"
                >
                  {metals.map(metal => (
                    <MenuItem key={metal} value={metal}>
                      {metal.charAt(0).toUpperCase() + metal.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small" error={!!errors.karat}>
                <InputLabel>Karat</InputLabel>
                <Select
                  value={karats.find(k => k.toLowerCase() === String(formData.karat || '').toLowerCase()) || ''}
                  onChange={(e) => handleInputChange('karat', e.target.value)}
                  label="Karat"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {karats.map(karat => (
                    <MenuItem key={karat} value={karat}>
                      {karat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Weight"
                value={formData.weight}
                onChange={(e) => handleInputChange('weight', parseFloat(e.target.value))}
                error={!!errors.weight}
                helperText={errors.weight}
                inputProps={{ min: 0.1, step: 0.1 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">g</InputAdornment>
                }}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Making Charges"
                value={formData.makingCharges}
                onChange={(e) => handleInputChange('makingCharges', parseFloat(e.target.value))}
                inputProps={{ min: 0, step: 0.01 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">KWD</InputAdornment>
                }}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Dimensions"
                value={formData.specifications.dimensions}
                onChange={(e) => handleInputChange('specifications.dimensions', e.target.value)}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Style"
                value={formData.specifications.style}
                onChange={(e) => handleInputChange('specifications.style', e.target.value)}
                size="small"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Inventory & Pricing */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Inventory & Pricing
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Quantity"
                value={formData.inventory.quantity}
                onChange={(e) => handleInputChange('inventory.quantity', parseInt(e.target.value))}
                inputProps={{ min: 0 }}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Low Stock Threshold"
                value={formData.inventory.lowStockThreshold}
                onChange={(e) => handleInputChange('inventory.lowStockThreshold', parseInt(e.target.value))}
                inputProps={{ min: 0 }}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Discount"
                value={formData.pricing.discount}
                onChange={(e) => handleInputChange('pricing.discount', parseFloat(e.target.value))}
                inputProps={{ min: 0, max: 100, step: 0.1 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>
                }}
                size="small"
              />
            </Grid>
          </Grid>

          <Stack spacing={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.inventory.inStock}
                  onChange={(e) => handleInputChange('inventory.inStock', e.target.checked)}
                />
              }
              label="Product In Stock"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.pricing.isOnSale}
                  onChange={(e) => handleInputChange('pricing.isOnSale', e.target.checked)}
                />
              }
              label="Currently On Sale"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => handleInputChange('isActive', e.target.checked)}
                />
              }
              label="Product Active"
            />
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          {loading ? 'Saving...' : product ? 'Update Product' : 'Create Product'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Simple Product Card Component
const ProductCard = ({ product, onEdit, onDelete, availableCategories }) => {
  const categoryName = availableCategories.find(cat => cat._id === product.category)?.name?.en || 'N/A';

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              {product.name?.en || 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {product.name?.ar || 'N/A'}
            </Typography>
            <Chip
              label={categoryName}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
          <Chip
            label={product.isActive ? 'Active' : 'Inactive'}
            size="small"
            color={product.isActive ? 'success' : 'default'}
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {product.metal}/{product.karat} • {product.weight}g
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
            {product.currentPrice ? `${product.currentPrice.toFixed(3)} KWD` : 'N/A'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Stock: {product.inventory?.quantity || 0}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              onClick={() => onEdit(product)}
              size="small"
              color="primary"
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={() => onDelete(product)}
              size="small"
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// Main Product Management Component
const Product = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // View mode state
  const [viewMode, setViewMode] = useState(isMobile ? 'card' : 'table');

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    metal: '',
    karat: '',
    isActive: ''
  });
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Dialog states
  const [formOpen, setFormOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);

  // Categories
  const [availableCategories, setAvailableCategories] = useState([]);

  // Update view mode based on screen size
  useEffect(() => {
    setViewMode(isMobile ? 'card' : 'table');
  }, [isMobile]);

  // Fetch products
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await api.getProducts({
        page,
        limit: 20,
        ...filters
      });
      if (response.success) {
        setProducts(response.data);
        setTotalPages(response.pagination.pages);
        setTotal(response.pagination.total);
        setError('');
      } else {
        setError(response.error || 'Failed to fetch products');
      }
    } catch (err) {
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/admin/category?isActive=true&limit=100`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableCategories(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [page, filters]);

  // Handle product save
  const handleSaveProduct = async (productData) => {
    setSaveLoading(true);
    try {
      let response;
      if (selectedProduct) {
        response = await api.updateProduct(selectedProduct._id, productData);
      } else {
        response = await api.createProduct(productData);
      }

      if (response.success) {
        setSuccess(selectedProduct ? 'Product updated successfully' : 'Product created successfully');
        setFormOpen(false);
        setSelectedProduct(null);
        fetchProducts();
      } else {
        setError(response.error || 'Failed to save product');
      }
    } catch (err) {
      setError('Network error occurred');
    } finally {
      setSaveLoading(false);
    }
  };

  // Handle product deletion
  const handleDeleteProduct = async (permanent = false) => {
    if (!productToDelete) return;

    setLoading(true);
    try {
      const response = permanent
        ? await api.permanentDeleteProduct(productToDelete._id)
        : await api.deleteProduct(productToDelete._id);

      if (response.success) {
        setSuccess(permanent
          ? 'Product deleted permanently'
          : 'Product hidden from the website. It still appears here as Inactive.');
        setDeleteDialogOpen(false);
        setProductToDelete(null);
        fetchProducts();
      } else {
        setError(response.error || 'Failed to delete product');
      }
    } catch (err) {
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1);
  };

  // Clear all filters
  const handleClearFilters = () => {
    setFilters({
      search: '',
      category: '',
      metal: '',
      karat: '',
      isActive: ''
    });
    setPage(1);
  };

  const metals = ['gold', 'silver'];
  const karats = ['18K', '21K', '22K', '24K', '925'];
  const hasActiveFilters = Object.values(filters).some(value => value !== '');

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        justifyContent: 'space-between',
        gap: 2,
        mb: 3
      }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            Product Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your jewelry collection
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {!isSmallMobile && (
            <Box sx={{ display: 'flex', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <IconButton
                onClick={() => setViewMode('table')}
                color={viewMode === 'table' ? 'primary' : 'default'}
                sx={{ borderRadius: 0 }}
              >
                <ViewListIcon />
              </IconButton>
              <IconButton
                onClick={() => setViewMode('card')}
                color={viewMode === 'card' ? 'primary' : 'default'}
                sx={{ borderRadius: 0 }}
              >
                <ViewModuleIcon />
              </IconButton>
            </Box>
          )}

          <Button
            variant="contained"
            onClick={() => {
              setSelectedProduct(null);
              setFormOpen(true);
            }}
            startIcon={<AddIcon />}
          >
            Add Product
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      <Collapse in={!!error}>
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Collapse>

      <Collapse in={!!success}>
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>
      </Collapse>

      {/* Mobile Filters Drawer */}
      {isSmallMobile && (
        <Drawer
          anchor="bottom"
          open={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          PaperProps={{
            sx: { borderRadius: '16px 16px 0 0' }
          }}
        >
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6">Filters</Typography>
              <IconButton onClick={() => setFiltersOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Search products..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
                }}
              />

              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  label="Category"
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {availableCategories.map(cat => (
                    <MenuItem key={cat._id} value={cat._id}>
                      {cat.name?.en || cat.name?.ar || 'Unnamed Category'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Metal</InputLabel>
                  <Select
                    value={filters.metal}
                    onChange={(e) => handleFilterChange('metal', e.target.value)}
                    label="Metal"
                  >
                    <MenuItem value="">All Metals</MenuItem>
                    {metals.map(metal => (
                      <MenuItem key={metal} value={metal}>
                        {metal.charAt(0).toUpperCase() + metal.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel>Karat</InputLabel>
                  <Select
                    value={filters.karat}
                    onChange={(e) => handleFilterChange('karat', e.target.value)}
                    label="Karat"
                  >
                    <MenuItem value="">All Karats</MenuItem>
                    {karats.map(karat => (
                      <MenuItem key={karat} value={karat}>
                        {karat}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.isActive}
                  onChange={(e) => handleFilterChange('isActive', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>

              {hasActiveFilters && (
                <Button variant="outlined" onClick={handleClearFilters}>
                  Clear All Filters
                </Button>
              )}
            </Stack>
          </Box>
        </Drawer>
      )}

      {/* Desktop Filters */}
      {!isSmallMobile && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterIcon /> Filters
            </Typography>
            {hasActiveFilters && (
              <Button variant="outlined" onClick={handleClearFilters} size="small" startIcon={<ClearIcon />}>
                Clear All
              </Button>
            )}
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search products..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
                }}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  label="Category"
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {availableCategories.map(cat => (
                    <MenuItem key={cat._id} value={cat._id}>
                      {cat.name?.en || cat.name?.ar || 'Unnamed Category'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Metal</InputLabel>
                <Select
                  value={filters.metal}
                  onChange={(e) => handleFilterChange('metal', e.target.value)}
                  label="Metal"
                >
                  <MenuItem value="">All Metals</MenuItem>
                  {metals.map(metal => (
                    <MenuItem key={metal} value={metal}>
                      {metal.charAt(0).toUpperCase() + metal.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Karat</InputLabel>
                <Select
                  value={filters.karat}
                  onChange={(e) => handleFilterChange('karat', e.target.value)}
                  label="Karat"
                >
                  <MenuItem value="">All Karats</MenuItem>
                  {karats.map(karat => (
                    <MenuItem key={karat} value={karat}>
                      {karat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.isActive}
                  onChange={(e) => handleFilterChange('isActive', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Mobile Filter Button */}
      {isSmallMobile && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Products ({total})</Typography>
          <Button
            variant="outlined"
            onClick={() => setFiltersOpen(true)}
            startIcon={<FilterIcon />}
            size="small"
          >
            Filters
          </Button>
        </Box>
      )}

      {/* Products Content */}
      <Paper>
        {!isSmallMobile && (
          <Box sx={{ p: 3, pb: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Products ({total})</Typography>
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {viewMode === 'table' && !isMobile ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Specifications</TableCell>
                      <TableCell>Price</TableCell>
                      <TableCell>Stock</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {products.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                            No products found
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {hasActiveFilters ? 'Try adjusting your filters' : 'Get started by adding your first product'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      products.map((product) => (
                        <TableRow key={product._id} hover>
                          <TableCell>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                {product.name?.en || 'N/A'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {product.name?.ar || 'N/A'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={availableCategories.find(cat => cat._id === product.category)?.name?.en || 'N/A'}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {product.metal}/{product.karat}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {product.weight}g
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {product.currentPrice ? `${product.currentPrice.toFixed(3)} KWD` : 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              Qty: {product.inventory?.quantity || 0}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={product.isActive ? 'Active' : 'Inactive'}
                              size="small"
                              color={product.isActive ? 'success' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <IconButton
                                onClick={() => {
                                  setSelectedProduct(product);
                                  setFormOpen(true);
                                }}
                                size="small"
                                color="primary"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                onClick={() => {
                                  setProductToDelete(product);
                                  setDeleteDialogOpen(true);
                                }}
                                size="small"
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ p: 3 }}>
                {products.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                      No products found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {hasActiveFilters ? 'Try adjusting your filters' : 'Get started by adding your first product'}
                    </Typography>
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {products.map((product) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={product._id}>
                        <ProductCard
                          product={product}
                          onEdit={(product) => {
                            setSelectedProduct(product);
                            setFormOpen(true);
                          }}
                          onDelete={(product) => {
                            setProductToDelete(product);
                            setDeleteDialogOpen(true);
                          }}
                          availableCategories={availableCategories}
                        />
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(event, value) => setPage(value)}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Floating Action Button for Mobile */}
      {isSmallMobile && (
        <Fab
          color="primary"
          onClick={() => {
            setSelectedProduct(null);
            setFormOpen(true);
          }}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24
          }}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Product Form Dialog */}
      <ProductForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setSelectedProduct(null);
        }}
        product={selectedProduct}
        onSave={handleSaveProduct}
        loading={saveLoading}
        categories={availableCategories}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete "{productToDelete?.name?.en}"</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>Hide from website</strong> takes the product off the storefront but
            keeps it here, marked Inactive, so past orders still show what was bought.
            You can switch it back on at any time.
          </Typography>
          <Typography variant="body2" color="error">
            <strong>Delete permanently</strong> erases the product. Orders that contain
            it will no longer be able to show its details. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => handleDeleteProduct(false)}
            color="warning"
            variant="outlined"
            disabled={loading}
          >
            Hide from website
          </Button>
          <Button
            onClick={() => handleDeleteProduct(true)}
            color="error"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {loading ? 'Deleting...' : 'Delete permanently'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Product;